import React, { useState, useEffect, useCallback } from 'react';
import { FaArrowRight, FaArrowLeft, FaTimes, FaKeyboard, FaGamepad } from 'react-icons/fa';
import { useOverlay } from '../../contexts/FocusContext';
import './OnboardingTour.css';

export { hasCompletedOnboarding, resetOnboarding, recordOnboardingDockUse } from './onboardingProgress';

interface TourStep {
  id: string;
  targetId: string | null; // null for no spotlight (welcome/finish steps)
  title: string;
  description: string;
}

const tourSteps: TourStep[] = [
  {
    id: 'welcome',
    targetId: null,
    title: 'Welcome!',
    description: 'Use WASD keys to move around. Use your mouse to look around.',
  },
  {
    id: 'video-panel',
    targetId: 'tour-video-panel',
    title: 'Video Chat',
    description: 'Join in a group video call here.',
  },
  {
    id: 'chat-panel',
    targetId: 'tour-chat-panel',
    title: 'Text Chat',
    description: 'Send chat messages here. Press Enter to quickly open the chat input.',
  },
  {
    id: 'rooms-button',
    targetId: 'tour-rooms-button',
    title: 'Explore Rooms',
    description: 'Navigate to different areas and breakout rooms.',
  },
  {
    id: 'people-raised-hands',
    targetId: 'tour-people-button',
    title: 'People & raised hands',
    description:
      'See who is in the room. In breakout rooms, use Clap to applaud and the Hand button to raise or lower your hand; the hand queue appears here in order.',
  },
  {
    id: 'help-button',
    targetId: 'tour-help-button',
    title: 'Need Help?',
    description:
      'FAQ, support, replay this tutorial, and graphics settings—plus Keyboard and game controls for movement and shortcuts.',
  },
  {
    id: 'fullscreen-button',
    targetId: 'tour-fullscreen-button',
    title: 'Fullscreen Mode',
    description: 'Click here to expand the experience to fullscreen for a more immersive view.',
  },
  {
    id: 'finish',
    targetId: null,
    title: "You're Ready!",
    description: 'Enjoy exploring the experience! You can replay this tutorial anytime from the Help menu.',
  },
];

interface OnboardingTourProps {
  isVisible: boolean;
  onComplete: () => void;
  onSkip: () => void;
}

interface SpotlightPosition {
  top: number;
  left: number;
  width: number;
  height: number;
}

const OnboardingTour: React.FC<OnboardingTourProps> = ({ isVisible, onComplete, onSkip }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [spotlightPosition, setSpotlightPosition] = useState<SpotlightPosition | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);

  const step = tourSteps[currentStep];

  // Register as overlay when visible - FocusContext handles Unity capture
  useOverlay('onboarding-tour', isVisible);

  // Update spotlight position only (tooltip stays centered)
  const updateSpotlight = useCallback(() => {
    if (!step || !step.targetId) {
      setSpotlightPosition(null);
      return;
    }

    const targetElement = document.querySelector(`[data-tour-id="${step.targetId}"]`);
    if (!targetElement) {
      setSpotlightPosition(null);
      return;
    }

    const rect = targetElement.getBoundingClientRect();
    const padding = 8;

    setSpotlightPosition({
      top: rect.top - padding,
      left: rect.left - padding,
      width: rect.width + padding * 2,
      height: rect.height + padding * 2,
    });
  }, [step]);

  // Update spotlight when step changes or window resizes
  useEffect(() => {
    if (!isVisible) return;

    updateSpotlight();

    const handleResize = () => updateSpotlight();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isVisible, currentStep, updateSpotlight]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!isVisible) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleSkip();
      } else if (e.key === 'ArrowRight' || e.key === 'Enter') {
        handleNext();
      } else if (e.key === 'ArrowLeft') {
        handleBack();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isVisible, currentStep]);

  const handleNext = () => {
    if (isAnimating) return;

    if (currentStep < tourSteps.length - 1) {
      setIsAnimating(true);
      setCurrentStep(currentStep + 1);
      setTimeout(() => setIsAnimating(false), 300);
    } else {
      handleComplete();
    }
  };

  const handleBack = () => {
    if (isAnimating) return;

    if (currentStep > 0) {
      setIsAnimating(true);
      setCurrentStep(currentStep - 1);
      setTimeout(() => setIsAnimating(false), 300);
    }
  };

  const handleComplete = () => {
    setCurrentStep(0);
    onComplete();
  };

  const handleSkip = () => {
    setCurrentStep(0);
    onSkip();
  };

  if (!isVisible) return null;

  const isLastStep = currentStep === tourSteps.length - 1;
  const isFirstStep = currentStep === 0;

  return (
    <div className="onboarding-tour-overlay" style={{ zIndex: 100000 }}>
      {/* Dark overlay with spotlight cutout */}
      <div className="onboarding-backdrop">
        {spotlightPosition && (
          <div
            className="onboarding-spotlight"
            style={{
              top: spotlightPosition.top,
              left: spotlightPosition.left,
              width: spotlightPosition.width,
              height: spotlightPosition.height,
            }}
          />
        )}
      </div>

      {/* Tooltip card - always centered */}
      <div className="onboarding-tooltip-container">
        <div className="onboarding-tooltip">
          {/* Skip button */}
          <button
            className="onboarding-skip-button"
            onClick={handleSkip}
            onMouseDown={(e) => e.preventDefault()}
            title="Skip tutorial"
          >
            <FaTimes size={14} />
          </button>

          {/* Welcome icon for first/last step */}
          {/* {(step.id === 'welcome' || step.id === 'finish') && (
            <div className="onboarding-icon">
              {step.id === 'welcome' ? <FaGamepad size={32} /> : <FaKeyboard size={32} />}
            </div>
          )} */}

          {/* Content */}
          <h3 className="onboarding-title">{step.title}</h3>
          <p className="onboarding-description">{step.description}</p>

          {/* Step counter */}
          {/* <div className="onboarding-step-counter">
            Step {currentStep + 1} of {tourSteps.length}
          </div> */}

          {/* Navigation */}
          <div className="onboarding-navigation">
            <button
              className="onboarding-nav-button onboarding-back-button"
              onClick={handleBack}
              onMouseDown={(e) => e.preventDefault()}
              disabled={isFirstStep}
              style={{ visibility: isFirstStep ? 'hidden' : 'visible' }}
            >
              <FaArrowLeft size={12} />
              Back
            </button>

            {/* Step indicators */}
            <div className="onboarding-dots">
              {tourSteps.map((_, index) => (
                <span
                  key={index}
                  className={`onboarding-dot ${index === currentStep ? 'active' : ''} ${index < currentStep ? 'completed' : ''}`}
                />
              ))}
            </div>

            <button
              className="onboarding-nav-button onboarding-next-button"
              onClick={handleNext}
              onMouseDown={(e) => e.preventDefault()}
            >
              {isLastStep ? 'Finish' : 'Next'}
              {!isLastStep && <FaArrowRight size={12} />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingTour;
